import { LAYERS } from 'common/layers'
import { MoblieLocationType } from 'common/types'
import { MapMouseEvent } from 'maplibre-gl'
import React from 'react'
import { useGlobalState } from 'state'
import mobileLocationPng from 'assets/mobileLocation.png'
import { Button, Dialog, DialogActions, DialogContent, IconButton, Paper, Stack, TextField, Typography } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import { grey } from '@mui/material/colors'


export const MobileLocationLayer = () => {
  const state = useGlobalState()
  const { mobileLocations, mobileLocationAddMode, setMobileLocations, setMobileLocationAddMode } = state.app
  const sourceId = LAYERS.MOBILE_LOCATION.id
  const layerId = LAYERS.MOBILE_LOCATION.id
  const iconId = `${sourceId}_mobile-location`
  const [open, setOpen] = React.useState(false)
  const [targetLocation, setTargetLocation] = React.useState<MoblieLocationType | null>(null)
  const isAddMode = React.useMemo(() => {
    return mobileLocationAddMode && targetLocation && targetLocation.id === 0
  },[mobileLocationAddMode, targetLocation])

  React.useEffect(() => {
    if (!state.mapbox) return
    state.map.loadImage(iconId, mobileLocationPng, false)
    state.map.setLayoutVisibility(layerId, true)
    state.mapbox?.on('click', layerClickListener)
    state.mapbox?.on('click', layerId, symbolClickListener)
    return () => {
      state.mapbox?.off('click', layerClickListener)
      state.mapbox?.off('click', layerId, symbolClickListener)
      state.map.setLayoutVisibility(layerId, false)
    }
  }, [state.mapbox])

  React.useEffect(() => {
    if (!state.mapbox) return
    const featres = mobileLocations.map((location: MoblieLocationType) => {
      return {
        type: 'Feature',
        properties: {
          id: location.id,
          name: location.name,
          icon: iconId,
        },
        geometry: {
          type: 'Point',
          coordinates: [location.lng, location.lat]
        }
      }
    })
    state.map.updateSourceData(sourceId,
      {
        type: 'FeatureCollection',
        features: featres
      })
  }, [mobileLocations, state.mapbox])

  React.useEffect(() => {
    if (!state.mapbox) return
    state.mapbox?.on('click', layerClickListener)
    state.mapbox?.on('click', layerId, symbolClickListener)
    return () => {
      state.mapbox?.off('click', layerClickListener)
      state.mapbox?.off('click', layerId, symbolClickListener)
    }
  }, [mobileLocationAddMode, mobileLocations])

  const layerClickListener = (e: MapMouseEvent) => {
    if (!state.mapbox) return
    if (!mobileLocationAddMode) return
    e.preventDefault()
    setTargetLocation({
      id: 0,
      name: `位置${mobileLocations.length + 1}`,
      lng: e.lngLat.lng,
      lat: e.lngLat.lat
    })
    setOpen(true)
  }

  const symbolClickListener = (e: any) => {
    if (!state.mapbox) return
    if (mobileLocationAddMode) return
    e.preventDefault()
    setMobileLocationAddMode(false)
    if (e.features.length === 0) return
    const feature = e.features[0]
    const location = mobileLocations.find(loc => loc.id === feature.properties.id)
    if (!location) return
    setTargetLocation(location)
    setOpen(true)
  }

  const saveLocaton = () => {
    if (!targetLocation) return
    if (!targetLocation.name) return

    const locations = [...mobileLocations]
    if (targetLocation.id === 0) {
      targetLocation.id = Date.now()
      locations.push(targetLocation)
    } else {
      const existingIndex = locations.findIndex(loc => loc.id === targetLocation.id)
      if (existingIndex !== -1) {
        locations[existingIndex] = targetLocation
      }
    }
    setMobileLocations(locations)
    localStorage.setItem('mobileLocations', JSON.stringify(locations))
    setMobileLocationAddMode(false)
    setOpen(false)
    setTargetLocation(null)
  }

  const deleteLocation = () => {
    if (!targetLocation) return
    if (confirm('選択した位置を削除しますか？') !== true) return
    const locations = mobileLocations.filter(loc => loc.id !== targetLocation.id)
    setMobileLocations(locations)
    localStorage.setItem('mobileLocations', JSON.stringify(locations))
    setMobileLocationAddMode(false)
    setOpen(false)
    setTargetLocation(null)
  }

  return (
    <>
      { mobileLocationAddMode && (
        <Stack sx={{position: 'absolute', zIndex: 1, top: 'calc(50% - 50px)', left: '50%', transform: 'translate(-50%, -50%)', pointerEvents: 'none'}} width={260}>
          <Paper sx={{
            fontSize: 14,
            color: grey[700],
            fontWeight: 'bold',
            whiteSpace: 'pre-wrap',
            textAlign: 'center',
            p: 1,
            backgroundColor: 'rgb(255, 255, 255, 0.9)',
          }}>
            {'登録する位置をタップして下さい'}
          </Paper>
        </Stack>
      )}

      {targetLocation && (
        <Dialog open={open} onClose={() => setOpen(false)}>
          <DialogContent sx={{minWidth: 260}}>
            <Stack mb={3} spacing={1}>
              <Typography variant='body2'>{isAddMode ? '選択した位置を登録します。' : '選択した位置を編集します。'}</Typography>
              { targetLocation && (
                <Typography variant='caption'>{targetLocation.lng.toFixed(5)}, {targetLocation.lat.toFixed(5)}</Typography>
              )}
              <Stack justifyContent={'flex-end'} direction='row'>
                { isAddMode && <Button size='small' sx={{width: 110, p: 0}} variant='outlined' onClick={() => setOpen(false)}>別の場所にする</Button> }
              </Stack>
            </Stack>
            <TextField
              size='small'
              autoComplete='off'
              margin="dense"
              label="名称"
              type="text"
              fullWidth
              variant="outlined"
              value={targetLocation.name}
              onChange={(e) => setTargetLocation({...targetLocation, name: e.target.value})}
              placeholder='位置の名称を入力'
              helperText={!targetLocation.name && open ? '名称を入力してください' : ''}
              error={!targetLocation.name && open}
            />
          </DialogContent>
          <DialogActions>
            { !isAddMode && (
              <IconButton size='small' onClick={deleteLocation} color='error' sx={{mr: 'auto'}}>
                <DeleteIcon />
              </IconButton>
            )}
            <Button onClick={() => {setMobileLocationAddMode(false); setOpen(false)}} color='primary' variant='text' size='small'>キャンセル</Button>
            <Button onClick={saveLocaton} color='primary' variant='contained' size='small'>{ isAddMode ? '登録' : '更新'}</Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  )
}
